"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Review = {
  initial: string;
  name: string;
  location: string;
  text: string;
};
const reviews: Review[] = [{
  initial: "J",
  name: "John M.",
  location: "Carlisle",
  text: "Absolute top quality plant repair service. Got our excavator back up and running in no time, saving us major downtime."
}, {
  initial: "S",
  name: "Stephen P.",
  location: "Penrith",
  text: "Fantastic service and very knowledgeable mechanics. Repaired our mini digger hydraulic issue quickly and at a fair price."
}, {
  initial: "M",
  name: "Mark D.",
  location: "Wigton",
  text: "Top-tier machinery maintenance. Always reliable, honest, and professional. Wouldn't use anyone else in Cumbria."
}, {
  initial: "A",
  name: "Andrew T.",
  location: "Kendal",
  text: "Brilliant customer service and quick turnaround on tool repairs. Highly recommend TM Plant to any trade professionals."
}, {
  initial: "R",
  name: "Robert K.",
  location: "Workington",
  text: "First class plant hire and repair. The team went above and beyond to sort out our generator issue on short notice."
}, {
  initial: "D",
  name: "David W.",
  location: "Cockermouth",
  text: "Extremely professional outfit. Their service engineers know plant machinery inside out. Brilliant workmanship."
}, {
  initial: "G",
  name: "Gary S.",
  location: "Whitehaven",
  text: "Reliable, efficient, and extremely thorough plant servicing. Excellent communication throughout the repair process."
}, {
  initial: "P",
  name: "Paul B.",
  location: "Brampton",
  text: "Great selection of equipment parts and outstanding repair workshop. Saved us thousands compared to buying new."
}, {
  initial: "C",
  name: "Craig H.",
  location: "Keswick",
  text: "Friendly team and exceptional standard of work on all our construction plant and machinery. Five stars!"
}, {
  initial: "L",
  name: "Liam R.",
  location: "Maryport",
  text: "Prompt response and expert diagnostic work. Got our tracked dumper sorted and back on site the very next day."
}];
export default function Visitorcounter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  const [shuffledReviews, setShuffledReviews] = useState<Review[]>(reviews);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  useEffect(() => {
    const shuffled = [...reviews].sort(() => Math.random() - 0.5);
    setShuffledReviews(shuffled);
  }, []);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentReviewIndex(prev => (prev + 1) % shuffledReviews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [shuffledReviews.length]);
  const handlePrev = () => {
    setCurrentReviewIndex(prev => (prev - 1 + shuffledReviews.length) % shuffledReviews.length);
  };
  const handleNext = () => {
    setCurrentReviewIndex(prev => (prev + 1) % shuffledReviews.length);
  };
  const currentReview = shuffledReviews[currentReviewIndex] || reviews[0];
  return <section data-section-id="3679" id="visitorcounter" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div ref={ref} className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-4 py-1.5 text-sm font-medium flex items-center gap-1.5 rounded-lg shadow-sm">
              <span className="flex items-center text-amber-500">
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
              </span>
              <span>5.0 from 77 reviews</span>
            </Badge>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
            What Our Customers Say
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">Read testimonials from homeowners and trade professionals across Cumbria who trusted us with their machinery and equipment.</p>
        </div>

        <div className="relative mt-16 md:mt-24 max-w-2xl mx-auto">
          <div className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <AnimatePresence mode="wait">
              <motion.div key={currentReviewIndex} initial={{
              opacity: 0,
              scale: 0.95,
              y: 10
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95,
              y: -10
            }} transition={{
              duration: 0.4,
              ease: "easeInOut"
            }}>
                <Card className="h-full flex flex-col justify-between bg-card p-8 rounded-lg shadow border-border">
                  <CardContent className="p-0 flex flex-col h-full justify-between space-y-6">
                    <div>
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, sIndex) => <Star key={sIndex} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
                      </div>
                      <p className="font-serif text-base italic leading-relaxed text-foreground">
                        "{currentReview.text}"
                      </p>
                    </div>

                    {(currentReview.initial || currentReview.name || currentReview.location) && <div className="flex items-center gap-4 pt-6 border-t border-border">
                        {currentReview.initial && <SafeHydrate><Avatar className="h-12 w-12 rounded-lg bg-primary/10 text-primary font-semibold flex items-center justify-center">
                            <AvatarFallback className="rounded-lg bg-primary/10 text-primary font-bold">
                              {currentReview.initial}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>}
                        <div>
                          {currentReview.name && <h3 className="font-semibold text-foreground text-base">{currentReview.name}</h3>}
                          {currentReview.location && <p className="text-sm text-muted-foreground">{currentReview.location}</p>}
                        </div>
                      </div>}
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-center gap-4 mt-8">
              <Button variant="outline" size="icon" onClick={handlePrev} aria-label="Previous review" className="rounded-lg h-10 w-10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="text-sm font-medium text-muted-foreground">
                {currentReviewIndex + 1} / {shuffledReviews.length}
              </div>
              <Button variant="outline" size="icon" onClick={handleNext} aria-label="Next review" className="rounded-lg h-10 w-10">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}